<template>
  <div class="stats-cards">
    <!-- Row 1: Core pipeline stats -->
    <el-row :gutter="12">
      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#409eff"><DataLine /></el-icon>
            <span>Всего постов</span>
          </div>
          <div class="stat-value" v-loading="statsStore.loading">
            {{ formatNumber(statsStore.stats?.total_posts ?? 0) }}
          </div>
        </el-card>
      </el-col>

      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card flagged" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#e6a23c"><Warning /></el-icon>
            <span>На проверке</span>
          </div>
          <div class="stat-value flagged-value" v-loading="statsStore.loading">
            {{ formatNumber(statsStore.stats?.flagged ?? 0) }}
          </div>
        </el-card>
      </el-col>

      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card validated" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#67c23a"><CircleCheck /></el-icon>
            <span>Подтверждено</span>
          </div>
          <div class="stat-value validated-value" v-loading="statsStore.loading">
            {{ formatNumber(statsStore.stats?.validated ?? 0) }}
          </div>
        </el-card>
      </el-col>

      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card fp" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#f56c6c"><CircleClose /></el-icon>
            <span>Ложные сраб.</span>
          </div>
          <div class="stat-value fp-value" v-loading="statsStore.loading">
            {{ formatPercent(statsStore.stats?.false_positive_rate ?? 0) }}
          </div>
        </el-card>
      </el-col>
    </el-row>

    <!-- Row 2: Pipeline observability -->
    <el-row :gutter="12" style="margin-top: 12px">
      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card llm" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#9254de"><Cpu /></el-icon>
            <span>LLM-вызовы</span>
          </div>
          <div class="stat-value" v-loading="statsStore.loading">
            {{ formatNumber(statsStore.stats?.llm_calls_total ?? 0) }}
          </div>
          <div class="stat-subtags">
            <el-tag size="small" type="success" class="subtag">
              ✓ {{ formatNumber(statsStore.stats?.llm_calls_success ?? 0) }}
            </el-tag>
            <el-tag size="small" type="danger" class="subtag" v-if="(statsStore.stats?.llm_calls_failed ?? 0) > 0">
              ✗ {{ formatNumber(statsStore.stats?.llm_calls_failed ?? 0) }}
            </el-tag>
          </div>
        </el-card>
      </el-col>

      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card tokens" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#409eff"><Coin /></el-icon>
            <span>Токены</span>
          </div>
          <div class="stat-value" v-loading="statsStore.loading">
            {{ formatNumber(statsStore.stats?.llm_total_tokens ?? 0) }}
          </div>
          <div class="stat-subtext">
            {{ formatNumber(statsStore.stats?.llm_input_tokens ?? 0) }} in / {{ formatNumber(statsStore.stats?.llm_output_tokens ?? 0) }} out
          </div>
        </el-card>
      </el-col>

      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card scraped" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#e6a23c"><Download /></el-icon>
            <span>Скраплено</span>
          </div>
          <div class="stat-value scraped-value" v-loading="statsStore.loading">
            {{ formatNumber(statsStore.stats?.total_scraped ?? 0) }}
          </div>
        </el-card>
      </el-col>

      <el-col :xs="12" :sm="12" :md="6" :lg="6">
        <el-card class="stat-card filtered" shadow="hover" :body-style="{ padding: '14px' }">
          <div class="stat-header">
            <el-icon size="20" color="#67c23a"><Filter /></el-icon>
            <span>Отфильтровано</span>
          </div>
          <div class="stat-value filtered-value" v-loading="statsStore.loading">
            {{ formatNumber(filteredTotal) }}
          </div>
          <div class="stat-subprogress">
            <el-progress
              :percentage="Math.round(statsStore.stats?.filter_effectiveness_pct ?? 0)"
              :stroke-width="4"
              :show-text="false"
              :color="'#67c23a'"
            />
            <span class="progress-label">эффективность фильтров</span>
          </div>
        </el-card>
      </el-col>
    </el-row>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStatsStore } from '@/stores/stats'

const statsStore = useStatsStore()

const filteredTotal = computed(() =>
  (statsStore.stats?.keyword_skipped ?? 0) + (statsStore.stats?.ml_filter_skipped ?? 0)
)

function formatNumber(n: number): string {
  return n.toLocaleString('ru-RU')
}

function formatPercent(n: number): string {
  return `${(n * 100).toFixed(1)}%`
}

</script>

<style scoped>
.stats-cards {
  margin-bottom: 16px;
}

.stat-card {
  transition: transform 0.15s ease;
}

.stat-card:hover {
  transform: translateY(-2px);
}

.stat-header {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: var(--app-text-regular);
  font-weight: 700;
  margin-bottom: 6px;
}

.stat-value {
  font-size: 32px;
  font-weight: 800;
  color: var(--app-text-primary);
}

.flagged-value {
  color: #e6a23c;
}

.validated-value {
  color: #67c23a;
}

.fp-value {
  color: #f56c6c;
}

.scraped-value {
  color: #e6a23c;
}

.filtered-value {
  color: #67c23a;
}

.stat-subtags {
  display: flex;
  gap: 6px;
  margin-top: 6px;
}

.subtag {
  font-size: 11px;
}

.stat-subtext {
  font-size: 12px;
  color: var(--app-text-secondary);
  margin-top: 4px;
}

.stat-subprogress {
  margin-top: 6px;
}

.progress-label {
  font-size: 11px;
  color: var(--app-text-secondary);
}
</style>
